<script setup>
import { ref, computed } from 'vue';

// Prevent auto-inheritance of non-prop attributes to avoid Vue warnings
defineOptions({
    inheritAttrs: false
});
import { Building2, ChevronsUpDown } from 'lucide-vue-next';
import { useFrontegg } from '@frontegg/vue';
import { useUserStore } from '@/stores/user.store.js';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { DotSpinner } from '@/components/ui/dot-spinner';
import _ from 'lodash';

const props = defineProps({
    onSelect: {
        type: Function,
        required: true
    },
    onlyCurrentTenant: {
        type: Boolean,
        default: false
    },
    includeAllOption: {
        type: Boolean,
        default: false
    }
});

const userStore = useUserStore();
const frontegg = useFrontegg();

const selectedTenantId = ref(null);

// Helper function to search for a specific tenant in the hierarchy
const searchTenant = (tenants, tenantId) => {
    if (!tenants || tenants.length === 0) return null;

    let tenant = _.find(tenants, { tenant_id: tenantId });

    if (!tenant) {
        for (let t of tenants) {
            if (t.children && t.children.length > 0) {
                tenant = searchTenant(t.children, tenantId);
                if (tenant) break;
            }
        }
    }

    return tenant;
};

// Helper function to collect all tenant IDs recursively
const takeAllShownTenantIds = (tenants) => {
    if (!tenants || tenants.length === 0) return [];

    let tenantIds = [];

    for (let tenant of tenants) {
        tenantIds.push(tenant.tenant_id);

        if (tenant.children && tenant.children.length > 0) {
            tenantIds = tenantIds.concat(takeAllShownTenantIds(tenant.children));
        }
    }

    return tenantIds;
};

// Compute shown tenants based on props
const shownTenants = computed(() => {
    let tenants = userStore.tenants;

    if (!tenants || !tenants.length) return [];

    // Current tenant and its children only
    if (props.onlyCurrentTenant) {
        const currentTenant = searchTenant(tenants, frontegg.authState.user?.tenantId);
        tenants = currentTenant ? [currentTenant] : [];
    }

    // Include "All" option
    if (props.includeAllOption && takeAllShownTenantIds([tenants].flat()).length > 1) {
        tenants = [{ tenant_id: 'all', tenant_name: 'All' }, ...tenants];
    }

    return tenants.filter((x) => x);
});

// Flatten tenants for display with indentation levels
const flattenedTenants = computed(() => {
    const result = [];

    const flatten = (tenants, level = 0) => {
        for (const tenant of tenants) {
            result.push({
                ...tenant,
                level,
                isAll: tenant.tenant_id === 'all'
            });

            if (tenant.children && tenant.children.length > 0) {
                flatten(tenant.children, level + 1);
            }
        }
    };

    flatten(shownTenants.value);
    return result;
});

// Current selected tenant for display
const selectedTenant = computed(() => {
    const tenantId = selectedTenantId.value || frontegg.authState.user?.tenantId;
    
    // Handle "All" option
    if (tenantId === 'all') {
        return { tenant_id: 'all', tenant_name: 'All' };
    }
    
    let allTenants = userStore.tenants;
    
    if (!allTenants || allTenants.length === 0) return {};
    if (!tenantId) return {};

    return searchTenant(allTenants, tenantId) || {};
});

// Handle tenant selection
const handleTenantSelect = (tenantId) => {
    selectedTenantId.value = tenantId;

    if (tenantId === 'all') {
        const allTenantIds = takeAllShownTenantIds(shownTenants.value?.filter((t) => t.tenant_id !== 'all'));
        props.onSelect(allTenantIds);
    } else {
        props.onSelect([tenantId]);
    }
};

// Get indentation style for nested tenants
const getIndentStyle = (level) => {
    return {
        paddingLeft: `${12 + level * 20}px`,
        paddingRight: '12px',
        minHeight: '36px'
    };
};

// Calculate dynamic button width based on tenant name
const buttonWidth = computed(() => {
    const tenantName = selectedTenant.value?.tenant_name || 'Select Tenant';
    const baseWidth = 160; // increased minimum width
    const charWidth = 9; // slightly increased width per character
    const iconSpace = 50; // increased space for icon + chevron + padding
    const calculatedWidth = Math.min(280, Math.max(baseWidth, tenantName.length * charWidth + iconSpace));
    return `${calculatedWidth}px`;
});

// Calculate dropdown width based on longest tenant name and max indentation
const dropdownWidth = computed(() => {
    if (!flattenedTenants.value.length) return '260px';
    
    const maxLevel = Math.max(...flattenedTenants.value.map(t => t.level || 0));
    const longestName = Math.max(...flattenedTenants.value.map(t => t.tenant_name?.length || 0));
    
    // Base width + indentation space + character space + padding/icons + check icon space
    const baseWidth = 180; // increased base width
    const indentSpace = maxLevel * 16;
    const nameSpace = Math.min(longestName * 8, 180); // increased name space cap
    const checkIconSpace = 40; // space for check icon + padding
    const totalWidth = Math.min(340, Math.max(260, baseWidth + indentSpace + nameSpace + checkIconSpace));
    
    return `${totalWidth}px`;
});
</script>

<template>
    <div class="relative inline-block" v-bind="$attrs">
        <DropdownMenu>
            <DropdownMenuTrigger as-child>
                <Button
                    variant="outline"
                    role="combobox"
                    class="h-8 px-3 text-sm justify-between"
                    :style="{ width: buttonWidth }">
                    <div class="flex items-center gap-1.5 min-w-0">
                        <Building2 class="h-3.5 w-3.5 flex-shrink-0" />
                        <span v-if="userStore.tenants" class="truncate">
                            {{ selectedTenant?.tenant_name || 'Select Tenant' }}
                        </span>
                        <DotSpinner v-else :loading="true" class="w-3.5 h-3.5" />
                    </div>
                    <ChevronsUpDown class="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                :style="{ width: dropdownWidth }"
                class="max-h-[300px] overflow-y-auto"
                align="end"
                :avoid-collisions="true"
                :collision-padding="8">
                <div v-for="tenant in flattenedTenants" :key="tenant.tenant_id">
                    <DropdownMenuItem
                        @click="handleTenantSelect(tenant.tenant_id)"
                        :class="[
                            'cursor-pointer transition-colors',
                            {
                                'bg-accent': tenant.tenant_id === selectedTenant.tenant_id,
                                'hover:bg-accent/50': tenant.tenant_id !== selectedTenant.tenant_id
                            }
                        ]"
                        :style="getIndentStyle(tenant.level)">
                        <div class="flex items-center w-full">
                            <span 
                                :class="[
                                    'truncate text-sm',
                                    {
                                        'font-semibold': tenant.isAll || tenant.level === 0,
                                        'font-normal text-muted-foreground': tenant.level > 0 && !tenant.isAll
                                    }
                                ]">
                                {{ tenant.tenant_name }}
                            </span>
                        </div>
                    </DropdownMenuItem>
                </div>

                <DropdownMenuItem v-if="flattenedTenants.length === 0" disabled> No tenants available </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    </div>
</template>
